import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import csMark from "../assets/careersavvy-mark.png";

/* ---------- brand (official CareerSavvy mark) ---------- */
export function BrandMark({ size = 30 }) {
  return (
    <span className="brand-mark" style={{ width: size, height: size }}>
      <img src={csMark} alt="" width={size} height={size} />
    </span>
  );
}

export function Brand({ to = "/", light = false }) {
  return (
    <Link to={to} className="brand" style={light ? { color: "#ecf6f1" } : undefined}>
      <BrandMark />
      <span>
        Career<span style={{ color: "var(--accent)" }}>Savvy</span>
      </span>
    </Link>
  );
}

/* ---------- buttons ---------- */
export function Btn({ variant = "primary", size, full, loading, children, className = "", as, to, ...rest }) {
  const cls = [
    "btn",
    `btn-${variant}`,
    size ? `btn-${size}` : "",
    full ? "btn-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  if (to !== undefined) {
    return (
      <Link to={to} className={cls} {...rest}>
        {loading ? <span className="spinner" /> : null}
        {children}
      </Link>
    );
  }
  return (
    <button type="button" className={cls} disabled={loading || rest.disabled} {...rest}>
      {loading ? <span className="spinner" /> : null}
      {children}
    </button>
  );
}

/* ---------- surfaces ---------- */
export function Card({ pad = true, hover = false, className = "", children, ...rest }) {
  const cls = ["card", pad ? "card-pad" : "", hover ? "card-hover" : "", className].filter(Boolean).join(" ");
  return (
    <div className={cls} {...rest}>
      {children}
    </div>
  );
}

export function Badge({ tone = "plain", children, className = "" }) {
  return <span className={`badge badge-${tone} ${className}`}>{children}</span>;
}

/* ---------- forms ---------- */
export function Field({ label, hint, error, children }) {
  return (
    <div className="field">
      {label ? <span className="field-label">{label}</span> : null}
      {children}
      {error ? <span className="field-error">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </div>
  );
}

/* ---------- feedback ---------- */
export function Spinner({ lg }) {
  return <span className={`spinner${lg ? " spinner-lg" : ""}`} />;
}

export function Skel({ w = "100%", h = 14, r, style, className = "" }) {
  return <span className={`skel ${className}`} style={{ display: "block", width: w, height: h, borderRadius: r, ...style }} />;
}

export function Empty({ icon, title, sub, action }) {
  return (
    <div className="empty">
      {icon ? <div className="empty-icon">{icon}</div> : null}
      {title ? <div className="empty-title">{title}</div> : null}
      {sub ? <div className="empty-sub">{sub}</div> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export function Note({ tone = "info", children }) {
  return <div className={`note note-${tone}`}>{children}</div>;
}

/* ---------- match ring (score 0-100) ---------- */
export function MatchRing({ score, size = 46, label, title }) {
  const n = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const tone = n >= 75 ? "var(--match-strong)" : n >= 50 ? "var(--match-good)" : "var(--match-fair)";
  return (
    <span
      className={`match-ring${label ? " has-label" : ""}`}
      style={{ width: size, height: size }}
      title={title || `${n}% ${label || "match"}`}
    >
      <svg width={size} height={size}>
        {/* track must read against surface-1 AND surface-2 — it used to be
            surface-2, which made the ring look cut off inside soft panels */}
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--line-strong)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={tone}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - n / 100)}
        />
      </svg>
      <span className="ring-n">{n}</span>
      {label ? <span className="ring-label">{label}</span> : null}
    </span>
  );
}

/* ---------- overlays ---------- */
export function Sheet({ open, onClose, title, footer, children, width }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="sheet-scrim" onClick={onClose} />
      <div className="sheet" style={width ? { width } : undefined} role="dialog" aria-modal="true">
        <div className="sheet-head">
          <div className="t-h3">{title}</div>
          <button type="button" className="btn btn-quiet btn-sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer ? <div className="sheet-foot">{footer}</div> : null}
      </div>
    </>
  );
}

export function Dialog({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="dialog-scrim" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true">
        {children}
      </div>
    </div>
  );
}

/* ---------- hooks ---------- */
export function useClickOutside(onAway) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onAway();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onAway]);
  return ref;
}

/* ---------- misc ---------- */
export function initialsOf(nameOrEmail) {
  if (!nameOrEmail) return "?";
  const s = String(nameOrEmail).trim();
  const at = s.indexOf("@");
  const base = at > 0 ? s.slice(0, at) : s;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

/* ---------- dropdown ----------
   Native <select> renders its option list through the OS, so on Windows it
   drops a white menu with a blue highlight into the middle of a dark UI and
   no CSS can reach it. This is a button + listbox with the same contract
   (`value`, `onChange` receiving an event-like object), so call sites that
   used a <select> only need their <option>s turned into an `options` array. */
export function Select({ value, onChange, options = [], className = "", ariaLabel, placeholder }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const current = options.find((o) => String(o.value) === String(value));
  const label = current ? current.label : placeholder || "Select";

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setActive(options.findIndex((o) => String(o.value) === String(value)));
  }, [open, options, value]);

  const pick = (v) => {
    setOpen(false);
    /* event-like shape so existing `(e) => setX(e.target.value)` handlers work */
    if (onChange) onChange({ target: { value: v } });
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (options[active]) pick(options[active].value);
    }
  };

  return (
    <div className={`csel${open ? " open" : ""} ${className}`.trim()} ref={wrapRef}>
      <button
        type="button"
        className="csel-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKeyDown}
      >
        <span className="csel-val">{label}</span>
        <svg className="csel-chev" width="11" height="7" viewBox="0 0 12 8" aria-hidden="true">
          <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
      {open ? (
        <ul className="csel-list" role="listbox" ref={listRef} tabIndex={-1}>
          {options.map((o, i) => {
            const on = String(o.value) === String(value);
            return (
              <li key={`${o.value}-${i}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`csel-opt${on ? " on" : ""}${i === active ? " active" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(o.value)}
                >
                  {o.label}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
